import { Helmet } from 'react-helmet-async';

/**
 * Injects a schema.org `MusicEvent` JSON-LD block on the event detail page
 * so Google shows rich results — date, venue, price, availability — right
 * in the SERP. Rich Results Test:
 *   https://search.google.com/test/rich-results
 *
 * Only the FIRST/cheapest offer is emitted with `lowPrice`/`highPrice` on
 * the Event's `offers` field; every tier still gets its own individual
 * Offer entry so Google can surface exact prices when a user hovers.
 *
 * Props:
 *   event — the /api/events/:id response
 */
export default function EventJsonLd({ event }) {
  if (!event || !event.id) return null;

  const canonicalUrl = `https://unighttickets.com/events/${event.id}`;

  // Compose ISO start / end datetimes from event.date (YYYY-MM-DD) + event.time (HH:MM).
  // Missing time defaults to 22:00 IST (typical door), missing date skips schema entirely.
  if (!event.date) return null;
  const timePart = /^\d{2}:\d{2}$/.test(event.time || '') ? event.time : '22:00';
  const startIso = `${event.date}T${timePart}:00+05:30`;
  // Assume a 6-hour event window — matches typical night runs (10 pm – 4 am
  // IST). Keep the same +05:30 offset on both fields so validators don't
  // flag a mixed-timezone schema (which they do when we emit UTC endDate).
  const startDate = new Date(startIso);
  const endMs = startDate.getTime() + 6 * 60 * 60 * 1000;
  const pad = (n) => String(n).padStart(2, '0');
  const _e = new Date(endMs + 5.5 * 60 * 60 * 1000); // shift into IST wall clock
  const endIso = isNaN(_e.getTime())
    ? startIso
    : `${_e.getUTCFullYear()}-${pad(_e.getUTCMonth() + 1)}-${pad(_e.getUTCDate())}T${pad(_e.getUTCHours())}:${pad(_e.getUTCMinutes())}:00+05:30`;

  const tiers = Array.isArray(event.ticket_tiers) ? event.ticket_tiers : [];
  const prices = tiers.map((t) => Number(t.price)).filter((n) => Number.isFinite(n) && n >= 0);
  const lowPrice = prices.length ? Math.min(...prices) : undefined;
  const highPrice = prices.length ? Math.max(...prices) : undefined;

  const availabilityFor = (t) => {
    if (t.quantity_available === 0) return 'https://schema.org/SoldOut';
    if (event.sales_closed) return 'https://schema.org/SoldOut';
    return 'https://schema.org/InStock';
  };

  const offers = tiers.map((t) => ({
    '@type': 'Offer',
    name: t.name || 'Ticket',
    price: Number(t.price ?? 0),
    priceCurrency: 'INR',
    url: canonicalUrl,
    availability: availabilityFor(t),
    validFrom: new Date().toISOString(),
    category: 'primary',
  }));

  const performers = Array.isArray(event.lineup)
    ? event.lineup
        .filter((n) => typeof n === 'string' && n.trim())
        .map((name) => ({ '@type': 'PerformingGroup', name: name.trim() }))
    : [];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MusicEvent',
    name: event.title,
    description: (event.description || '').slice(0, 500),
    startDate: startIso,
    endDate: endIso,
    eventStatus: event.sales_closed
      ? 'https://schema.org/EventScheduled'
      : 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    image: event.image_url ? [event.image_url] : undefined,
    url: canonicalUrl,
    location: {
      '@type': 'Place',
      name: event.venue || 'TBA',
      address: {
        '@type': 'PostalAddress',
        streetAddress: event.address || undefined,
        addressLocality: event.city || undefined,
        addressCountry: event.country || 'IN',
      },
    },
    ...(performers.length ? { performer: performers } : {}),
    organizer: {
      '@type': 'Organization',
      name: 'Unight',
      url: 'https://unighttickets.com',
    },
    ...(offers.length
      ? {
          offers: offers.length === 1
            ? offers[0]
            : {
                '@type': 'AggregateOffer',
                priceCurrency: 'INR',
                lowPrice,
                highPrice,
                offerCount: offers.length,
                offers,
                availability: offers.some((o) => o.availability === 'https://schema.org/InStock')
                  ? 'https://schema.org/InStock'
                  : 'https://schema.org/SoldOut',
                url: canonicalUrl,
              },
        }
      : {}),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
